// Porté de src/sections/concept.js du prototype (refonte 2026).
import { arrowCursor } from '@oqp/arrow-cursor';

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

// Pillars: snake-like wave, front card = link (its hidden CTA), anywhere else on the stack = arrow cursor → next card.
// Phones: swipe or tap = next card, the visible CTA is the link.
function initConcept(zone) {
  if (!zone || zone.dataset.ready) return;
  zone.dataset.ready = 'true';
  const cards = [...zone.querySelectorAll('.pillar')];
  let order = cards.slice();               // order[0] is the front card
  let busy = false;

  // only the front card can be reached (its CTA)
  const apply = () => order.forEach((c, i) => {
    c.dataset.slot = i;
    c.inert = i !== 0;
  });
  apply();

  // dir: undefined = drop out in front (desktop); ±1 = thrown left/right (swipe, tap on phones)
  function next(dir) {
    if (busy) return;
    busy = true;
    const leaving = order[0];
    if (dir) {
      leaving.classList.add('is-thrown');
      leaving.style.translate = `calc(var(--x) + ${dir * 460}px) calc(var(--y) + 40px)`;
      leaving.style.rotate = `${dir * 18}deg`;
    } else leaving.classList.add('is-leaving');         // drops out in front…
    setTimeout(() => {
      order = [...order.slice(1), leaving];
      leaving.classList.remove('is-leaving', 'is-thrown');
      leaving.style.translate = leaving.style.rotate = '';
      apply();                                          // …then slides into the back slot, the others move up
      setTimeout(() => { busy = false; }, 700);
    }, reduced ? 0 : 330);
  }

  const swipeMode = matchMedia('(max-width: 560px)');
  const onFront = e => !!e.target.closest('.pillar[data-slot="0"]');
  let dragged = false;
  zone.addEventListener('click', e => {
    if (dragged) { e.preventDefault(); dragged = false; return; }   // a swipe isn't a tap
    if (e.target.closest('.pillar__cta, .pillars__next')) return;
    if (swipeMode.matches) return next(-1);
    if (!onFront(e)) return next();
    // front card: follow its CTA (the card itself isn't a link: it holds the CTA shown on phones)
    const cta = e.target.closest('.pillar').querySelector('.pillar__cta');
    if (!cta) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey) open(cta.href, '_blank');
    else location.href = cta.href;
  });
  zone.querySelector('.pillars__next')?.addEventListener('click', () => next());
  arrowCursor(zone, e => !onFront(e));

  // narrow screens: swipe the front card left or right (Tinder-like); a tap also goes to the next card
  let start = null;
  zone.addEventListener('pointerdown', e => {
    const card = e.target.closest('.pillar[data-slot="0"]');
    if (!swipeMode.matches || !card || busy) return;
    start = { x: e.clientX, y: e.clientY, card, dx: 0, id: e.pointerId };
    dragged = false;
  });
  zone.addEventListener('pointermove', e => {
    if (!start || e.pointerId !== start.id) return;
    const dx = e.clientX - start.x, dy = e.clientY - start.y;
    if (!dragged && Math.abs(dx) < 8) return;
    if (!dragged && Math.abs(dy) > Math.abs(dx)) { start = null; return; }   // vertical: let the page scroll
    if (!dragged) { dragged = true; start.card.classList.add('is-dragging'); start.card.setPointerCapture(e.pointerId); }
    start.dx = dx;
    const scale = parseFloat(getComputedStyle(zone).scale) || 1;               // the stack is scaled down on phones
    start.card.style.translate = `calc(var(--x) + ${dx / scale}px) var(--y)`;
    start.card.style.rotate = `${dx / 18}deg`;
  });
  const release = () => {
    if (!start) return;
    const { card, dx } = start;
    start = null;
    card.classList.remove('is-dragging');
    if (!dragged) return;
    if (Math.abs(dx) > 70) next(Math.sign(dx));                                  // far enough: throw it
    else { card.style.translate = card.style.rotate = ''; }                     // not enough: back in place
  };
  zone.addEventListener('pointerup', release);
  zone.addEventListener('pointercancel', release);

  // snake: every card rides the same sine wave, the front one leads, the others follow with a delay
  if (reduced) return;
  let visible = false;
  new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(zone);
  const inner = new Map(cards.map(c => [c, c.querySelector('.pillar__card')]));
  const T = 4200, A = 10, PHASE = .95;
  (function frame(t) {
    requestAnimationFrame(frame);
    if (!visible) return;
    order.forEach((c, i) => {
      const a = (t / T) * Math.PI * 2 - i * PHASE;
      inner.get(c).style.transform = `translate3d(0, ${Math.sin(a) * A}px, 0) rotate(${Math.cos(a) * .6}deg)`;
    });
  })(0);
}

document.querySelectorAll('[data-pillars]').forEach(initConcept);
document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('[data-pillars]').forEach(initConcept));
